import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetSalesInsightsAggregatedRequest, SalesInsights, SalesInsightsCompareResponse, useSalesInsightsCompare } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import NumericChartCompared from '../charts/numericChart/NumericChartCompared';

const ComparedSalesInsightAreaHospitality = () => {
  const { t } = useTranslation();
  const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
  const { companyConfig, refreshDate } = useContext(DashboardContext);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalStaff, setTotalStaff] = useState(0);

  useEffect(() => {
    setTotalDevices(companyConfig?.devices?.length || 0);
    setTotalStaff(companyConfig?.staff?.length || 0);
  }, [companyConfig])


  const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
    fields: 'transactionVolume,netSales,totalTax,avgTransactionValue,activeDevices,activeStaff,totalDiscount,activeCustomers,totalQuantity,totalServiceCharge,totalCustomerDiscount,totalItemDiscount,totalBasketDiscount,totalContributions,totalSales,totalAmountPaidByPoints,refunds,netMargin',
    startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
    endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
    compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
    compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
    locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
    locationArea: filters?.locationArea?.id
  });

  const {
    data: comparedSalesInsights,
    isLoading,
    isError,
    refetch: fetchSalesInsights
  } = useSalesInsightsCompare<SalesInsightsCompareResponse>(params, 'compared-sales-insights');

  const calculateDiscountPercent = (numerator = 0, denominator = 0): number => {
    if (denominator === 0) return NaN;
    const percent = (numerator / denominator);
    return percent;
  };

  const calculatePercentChanges = (
    current: number,
    compared: number
  ): { percent: number, percentCompared: number } => {
    if (!current && !compared) return { percent: NaN, percentCompared: NaN };

    const percent = current
      ? (current - compared) / current
      : NaN;

    const percentCompared = compared
      ? (compared - current) / compared
      : NaN;

    return { percent, percentCompared };
  };

  const calculateSplitPercent = (
    current: number,
    compared: number
  ): { percent: number | undefined, percentCompared: number | undefined } => {
    const total = current + compared;
    if (total === 0) return { percent: undefined, percentCompared: undefined };

    return {
      percent: current / total,
      percentCompared: compared / total,
    };
  };

  const getValues = useCallback((field: keyof SalesInsights, comparedField: keyof SalesInsights): [number, number] => {
    const current = comparedSalesInsights?.salesInsights?.[field] ?? 0;
    const compared = comparedSalesInsights?.comparedSalesInsights?.[comparedField] ?? 0;
    return [current, compared];
  }, [comparedSalesInsights]);

  const { percent: totalSalesIncPercent, percentCompared: totalSalesIncPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('totalSalesIncTax', 'totalSalesIncTax');
    return calculatePercentChanges(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.totalSalesIncTax,
    comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax
  ]);

  const { percent: netSalesIncPercent, percentCompared: netSalesIncPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('netSalesIncTax', 'netSalesIncTax');
    return calculatePercentChanges(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.netSalesIncTax,
    comparedSalesInsights?.comparedSalesInsights.netSalesIncTax
  ]);

  const { percent: transactionVolumePercent, percentCompared: transactionVolumePercentCompared } = useMemo(() => {
    const [current, compared] = getValues('transactionVolume', 'transactionVolume');
    return calculatePercentChanges(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.transactionVolume,
    comparedSalesInsights?.comparedSalesInsights.transactionVolume
  ]);

  const { percent: averageSalesPercent, percentCompared: averageSalesPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('avgTransactionValue', 'avgTransactionValue');
    return calculatePercentChanges(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.avgTransactionValue,
    comparedSalesInsights?.comparedSalesInsights.avgTransactionValue
  ]);

  const { percent: netMarginPercent, percentCompared: netMarginPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('netMargin', 'netMargin');
    return calculatePercentChanges(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.netRevenue,
    comparedSalesInsights?.comparedSalesInsights.netRevenue
  ]);

  const { percent: totalTaxPercent, percentCompared: totalTaxPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('totalTax', 'totalTax');
    return calculateSplitPercent(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.totalTax,
    comparedSalesInsights?.comparedSalesInsights.totalTax
  ]);

  const { percent: activeStaffPercent, percentCompared: activeStaffPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('activeStaff', 'activeStaff');
    return calculateSplitPercent(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.activeStaff,
    comparedSalesInsights?.comparedSalesInsights.activeStaff
  ]);

  const { percent: activeDevicesPercent, percentCompared: activeDevicesPercentCompared } = useMemo(() => {
    const [current, compared] = getValues('activeDevices', 'activeDevices');
    return calculateSplitPercent(current, compared);
  }, [
    comparedSalesInsights?.salesInsights.activeDevices,
    comparedSalesInsights?.comparedSalesInsights.activeDevices
  ]);

  const serviceChargePercent = useMemo(() =>
    calculateDiscountPercent(
      comparedSalesInsights?.salesInsights?.totalServiceCharge,
      comparedSalesInsights?.salesInsights?.totalDiscount
    )
    , [comparedSalesInsights?.salesInsights]);

  const serviceChargeComparedPercent = useMemo(() =>
    calculateDiscountPercent(
      comparedSalesInsights?.comparedSalesInsights?.totalServiceCharge,
      comparedSalesInsights?.comparedSalesInsights?.totalDiscount
    )
    , [comparedSalesInsights?.comparedSalesInsights]);


  useEffect(() => {
    if (refreshDate != null) {
      fetchSalesInsights();
    }
  }, [refreshDate]);

  useEffect(() => {
    setParams({
      ...params,
      startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
      endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
      compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
      compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
      locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
      locationArea: filters?.locationArea?.id
    });
  }, [filters]);

  useEffect(() => { fetchSalesInsights(); }, [params]);

  return (
    <Box sx={{ marginY: '20px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <NumericChartCompared
          title={t("screens.dashboarding.charts.sales")}
          subtitle={t("screens.dashboarding.charts.includingTax")}
          currency={comparedSalesInsights?.currency}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.totalSalesIncTax || 0}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax || 0}
          percent={totalSalesIncPercent}
          percentCompared={totalSalesIncPercentCompared}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
          buttonText={t("screens.dashboarding.charts.salesOverview")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.transactions")}
          subtitle={t("screens.dashboarding.charts.volume")}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.transactionVolume}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.transactionVolume}
          percent={transactionVolumePercent}
          percentCompared={transactionVolumePercentCompared}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_OAUTH_CALLBACK_URL}transactions`}
          buttonText={t("screens.dashboarding.charts.transactionsOverview")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.netSales")}
          subtitle={t("screens.dashboarding.charts.includingTax")}
          currency={comparedSalesInsights?.currency}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.netSalesIncTax || 0}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax || 0}
          percent={netSalesIncPercent}
          percentCompared={netSalesIncPercentCompared}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
          buttonText={t("screens.dashboarding.charts.salesOverview")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.netMargin")}
          subtitle={t("screens.dashboarding.charts.excludingTax")}
          currency={comparedSalesInsights?.currency}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.netMargin}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.netMargin || 0}
          percent={netMarginPercent}
          percentCompared={netMarginPercentCompared}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
          buttonText={t("screens.dashboarding.charts.salesOverview")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.staff")}
          subtitle={t("screens.dashboarding.charts.active")}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.activeStaff || 0}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.activeStaff || 0}
          percent={activeStaffPercent}
          percentCompared={activeStaffPercentCompared}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByEmployee.aspx`}
          buttonText={t("screens.dashboarding.charts.netSalesByStaff")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.devices")}
          subtitle={t("screens.dashboarding.charts.active")}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.activeDevices || 0}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.activeDevices || 0}
          percent={activeDevicesPercent}
          percentCompared={activeDevicesPercentCompared}
          isError={isError}
          isLoading={isLoading}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByDeviceGroup.aspx`}
          buttonText={t("screens.dashboarding.charts.salesByDevices")}
        />
        <NumericChartCompared
          title={t("screens.dashboarding.charts.discounts")}
          subtitle={t("screens.dashboarding.charts.includingTax")}
          currency={comparedSalesInsights?.currency}
          currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
          currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
          comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
          comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
          data={comparedSalesInsights?.salesInsights.totalDiscount}
          dataCompared={comparedSalesInsights?.comparedSalesInsights.totalDiscount}
          percent={serviceChargePercent}
          percentCompared={serviceChargeComparedPercent}
          isLoading={isLoading}
          isError={isError}
          linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/ServiceCharges.aspx'`}
          buttonText={t("screens.dashboarding.charts.serviceCharge")}
        />
      </Box>
    </Box>
  )
}

export default ComparedSalesInsightAreaHospitality;
